import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExperienceSkeleton from './ExperienceSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import LocalizedNavLink from '../../hooks/LocalizedNavLink';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './Experience.scss';

const Experience = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { data: mainData, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/mainData.json');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const mainContent = mainData?.TopMainData?.ExperienceSection;

        if (mainContent && 
            mainContent.contentAccent && 
            mainContent.contentTitle && 
            mainContent.contentSubtitle && 
            mainContent.items && 
            mainContent.items.length > 0) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, mainData]);

    const experienceData = useMemo(() => {
        return mainData?.TopMainData?.ExperienceSection || null;
    }, [mainData]);

    const getTranslation = useCallback((textObj) => {
        return textObj?.[currentLang] || textObj?.en || '';
    }, [currentLang]);

    if (isLoading) {
        return <ExperienceSkeleton />;
    }

    if (hasError || !experienceData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="home__other__services">
            <div className="left__side__other__services">
                <div className="other__services__content">
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <p className="other__services__accent">
                        {getTranslation(experienceData.contentAccent)}
                    </p>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                    <h4 className="other__services__title">
                        {getTranslation(experienceData.contentTitle)}
                    </h4>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={200}>
                    <h5 className="other__services__subtitle">
                        {getTranslation(experienceData.contentSubtitle)}
                    </h5>
                    </AnimatedOnScroll>
                </div>
            </div>
            <div className='right__side__other__services'>
                {experienceData.items.map((item, index) => (
                    <React.Fragment key={index}>
                        <div className='right__side__other__services__item'>
                            <AnimatedOnScroll animation="fade-up" delay={0}>
                            <img 
                                src={`${IMAGES_BASE_URL}${item.image}`}
                                onError={(e) => console.log('Failed to load:', e.target.src)}
                                className='other__services__item__pic' 
                                alt={getTranslation(item.text)} 
                                loading="lazy"
                            />
                            </AnimatedOnScroll>
                        </div>
                        <div className='right__side__other__services__item'>
                            <LocalizedNavLink 
                                className='other__services__item__link' 
                                to={item.link}
                            >
                                <AnimatedOnScroll animation="fade-up" delay={0}>
                                <h4 className='other__services__item__title'>
                                    {getTranslation(item.text)}
                                </h4>
                                <p className='other__services__item__text'>
                                    {getTranslation(item.subtitle)}
                                </p>
                                </AnimatedOnScroll>
                            </LocalizedNavLink>
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default React.memo(Experience);